/**
 * sandbox
 *
 * @author    Richard J. Turner <richard.turner@mediatel.co.uk>
 * @copyright 2015 Mediatel Limited.
 */
"use strict";

/**
 *
 * @param {Core} core
 * @param {string} moduleId
 * @returns {Sandbox}
 */
export default (core, moduleId) => {
    const container = core.dom.query("#" + moduleId);

    /**
     * @typedef {object} Sandbox
     * @property {function} log
     * @property {function} find
     * @property {function} findAncestor
     * @property {function} empty
     * @property {function} append
     * @property {function} addEvent
     * @property {function} removeEvent
     * @property {function} notify
     * @property {function} listen
     * @property {function} ignore
     * @property {function} get
     * @property {function} post
     * @property {function} postApi
     * @property {function} chart
     * @property {function} datePicker
     */
    return {
        log(severity, message) {
            core.log(severity, message);
        },

        /**
         * @param {string} selector
         * @return jQuery-like object wrapping the selected element.
         */
        find(selector) {
            return container.query(selector);
        },

        /**
         * @param {HTMLElement} element
         * @param {string} selector
         * @return jQuery-like object wrapping the selected element.
         */
        findAncestor(element, selector) {
            return core.dom.ancestor(element, selector);
        },

        /**
         * @param {HTMLElement} element
         * @return {Sandbox}
         */
        empty(element) {
            core.dom.empty(element);
            //noinspection JSValidateTypes
            return this;
        },

        create(html) {
            return core.dom.create(html);
        },

        /**
         * @param {HTMLElement} element
         * @param {string|HTMLElement} child
         */
        append(element, child) {
            core.dom.append(element, child);
        },

        /**
         * @param {HTMLElement} element
         * @param {string|HTMLElement} child
         */
        show(element) {
            core.dom.show(element);
        },

        /**
         * @param {HTMLElement} element
         * @param {string|HTMLElement} child
         */
        hide(element) {
            core.dom.hide(element);
        },

        /**
         * @param {HTMLElement} element
         * @param {object} event
         * @param {function} handler
         */
        addEvent(element, event, handler) {
            core.dom.bind(element, event, handler);
        },

        /**
         * @param {HTMLElement} element
         * @param {string} eventName
         * @param {function} handler
         */
        removeEvent(element, eventName, handler) {
            core.dom.unbind(element, eventName, handler);
        },

        /**
         * @param {object} event
         */
        notify(event) {
            if (event.type) {
                core.triggerEvent(event);
            }
        },

        /**
         * @param {object} events Map of event names to listener functions
         */
        listen(events) {
            core.registerEvents(events, moduleId);
        },

        /**
         * @param {Array<string>} events List of events to ignore
         */
        ignore(events) {
            core.removeEvents(events, moduleId);
        },

        /**
         * GETs an XHR to the specified URL.
         * @param {string} url
         * @returns {Promise}
         */
        get(url) {
            return core.get(url);
        },

        /**
         * POSTs an XHR to the specified URL, returning a Promise
         * @param {string} url
         * @param {object} data
         * @returns {Promise}
         */
        post(url, data) {
            return core.post(url, data);
        },

        /**
         * POSTS an XHR to the BARB API
         * @param {string} url
         * @param {object} data
         * @returns {Promise}
         */
        postApi(url, data = {}) {
            return jQuery.ajax({
                type: (data.method) ? data.method : "POST" ,
                url: api_url + url,
                data: data
            })
        },

        /**
         * Generates a chart and sets it as the body of the specified element
         * @param {HTMLElement} element
         * @param {object} options Chart options
         */
        chart(element, options) {
            core.dom.chart(element, options);
        },

        datePicker(element, options) {
            core.dom.datePicker(element, options);
        }
    };
};
